import clsx from 'clsx'

import { FvLinkButton, InlineSlashSeparated } from '@fv/client-components'
import type { ClientAddressLookupResult } from '@fv/client-types'

import { ActionList, ActionListItem } from '@/components/ActionList'
import { ListItem } from '@/components/List'

import { HasPermission } from '../auth'
import { useRole } from '../auth/hooks'
import type { SetAddress } from './types'
import { useAddressBook, useAddressBookConfig } from './useAddressBook'
import { useDeleteAddress } from './useDeleteAddress'

type Props = {
  address: ClientAddressLookupResult
  selectAddress?: SetAddress
  isRemovable?: boolean
  listHasRemove?: boolean
}

export const AddressBookEntry = ({
  address,
  selectAddress,
  isRemovable,
  listHasRemove,
}: Props) => {
  const role = useRole()
  const userCanRemove = role === 'shipper' || !address.isAccountLocation
  const config = useAddressBookConfig()
  const allowEdits = useAddressBook(s => !s.config.postalCodeFilter)
  const edit = useAddressBook(s => s.edit)
  const remove = useDeleteAddress(config)

  const handleDeleteClick = async () => {
    if (
      !address._id ||
      !window.confirm(
        'Are you sure you want to remove this address book entry?',
      )
    ) {
      return
    }
    return await remove.mutateAsync({ id: address._id })
  }

  const {
    adminArea3,
    address: street,
    city,
    company,
    isAccountLocation,
    postalCode,
    state,
  } = address

  return (
    <ListItem
      className={`gap-x-2 hover:!border-fv-blue hover:bg-white hover-list-item transition-colors duration-100 ${isAccountLocation ? 'hover:!border-fv-orange hover-list-item--priority' : 'hover:!border-fv-blue'}`}
      custom
    >
      <FvLinkButton
        className="py-4 flex-1 truncate max-w-full"
        icon={isAccountLocation ? 'industry-alt' : 'building'}
        iconClass={clsx({ 'text-fv-orange-bright': isAccountLocation })}
        onClick={() =>
          selectAddress ? selectAddress(address) : edit(address._id)
        }
      >
        <InlineSlashSeparated>
          {!!company && <span>{company}</span>}
          {!!street && <span>{street}</span>}
          {!!adminArea3 && <span>{adminArea3}</span>}
          {!!(city || state || postalCode) && (
            <span>
              {city} {state} {postalCode}
            </span>
          )}
        </InlineSlashSeparated>
      </FvLinkButton>
      <HasPermission name="manage-addresses">
        <ActionList className="ml-auto">
          {allowEdits && address._id && (
            <ActionListItem
              icon="pen-alt"
              onClick={() => {
                if (!address._id) return
                edit(address._id)
              }}
            />
          )}

          <div
            className={clsx(
              listHasRemove && address._id ? 'visible' : 'hidden',
            )}
          >
            <ActionListItem
              icon="trash"
              iconClass={clsx(
                isRemovable && userCanRemove
                  ? 'text-fv-orange-bright'
                  : 'invisible',
              )}
              onClick={handleDeleteClick}
            />
          </div>
        </ActionList>
      </HasPermission>
    </ListItem>
  )
}
